import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Button2 from '@embracesbs/component-button2/Button2'
import Icon from '@embracesbs/component-icon'
import Text from '@embracesbs/component-text'
import TextField from '@embracesbs/component-textfield'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper/TextFieldWrapper'
import Modal from '@embracesbs/component-modal'
import EmptyState from '@embracesbs/component-emptystate'
import Toast, { customToast } from '@embracesbs/component-toast'
import { useApi } from '../Hooks'
import { AuthRoleContext } from '../Components/Auth/AuthRoleContext'
import LoadingState from '../Components/Common/LoadingState'
import BandBackButton from '../Components/Common/BandBackButton'
import CertificateIssuersHelp from '../Components/Certificates/CertificateIssuersHelp'
import { CertificateIssuer, CertificateIssuerClusterInfo, ClusterIssuerState } from '../Interfaces/Types'

const APP_FONT = "'F-Default', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

// Same rules the API enforces.
const ISSUER_NAME_PATTERN = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?(\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*$/
const MAX_NAME_LENGTH = 240
const HMAC_PATTERN = /^[A-Za-z0-9_-]+={0,2}$/

const bandStyle: CSSProperties = {
  flexShrink: 0,
  height: '52px',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '0 16px',
  backgroundColor: 'var(--tma-band-bg)'
}

const toolbarStyle: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '12px',
  background: 'var(--tma-panel-bg)',
  borderBottom: '1px solid var(--tma-list-card-border)',
  padding: '10px 16px'
}

const thStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: 'var(--tma-card-bg)',
  borderBottom: '1px solid var(--tma-list-card-border)',
  padding: '8px 12px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--tma-text-strong)'
}

const tdStyle: CSSProperties = {
  borderBottom: '1px solid var(--tma-card-border)',
  padding: '10px 12px',
  fontSize: '13px',
  verticalAlign: 'top',
  color: 'var(--tma-text-strong)'
}

const monoStyle: CSSProperties = { fontFamily: MONO_FONT, fontSize: '12px', color: 'var(--tma-text-muted)', wordBreak: 'break-all' }

// Status tokens so the accessibility themes can substitute hues (index.css).
// These are var() strings: derive tints with color-mix, never hex suffixes.
const pill = (color: string): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  color,
  background: `color-mix(in srgb, ${color} 12%, transparent)`,
  borderRadius: '999px',
  padding: '2px 8px',
  fontSize: '12px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  border: 'none',
  fontFamily: APP_FONT
})

const CLUSTER_STATE: Record<ClusterIssuerState, { label: string; color: string; hint: string }> = {
  Ready: { label: 'Ready', color: 'var(--tma-status-ok-strong)', hint: 'cert-manager registered the ACME account.' },
  NotReady: { label: 'Not ready', color: 'var(--tma-warning-text)', hint: 'cert-manager has not registered the ACME account.' },
  Missing: {
    label: 'Not in cluster',
    color: 'var(--tma-danger-text)',
    hint: 'The ClusterIssuer is missing from the cluster. Re-apply it.'
  },
  NotManaged: {
    label: 'Not managed by TMA',
    color: 'var(--tma-danger-text)',
    hint: 'A ClusterIssuer with this name exists but was not created by TMA.'
  },
  Unknown: { label: 'Unknown', color: 'var(--tma-text-muted)', hint: 'The cluster could not be reached.' },
  Disabled: { label: 'Not synced', color: 'var(--tma-text-muted)', hint: 'Cluster sync is switched off in this environment.' }
}

interface IssuerForm {
  name: string
  displayName: string
  description: string
  acmeServerUrl: string
  eabKeyId: string
  eabHmacKey: string
}

const EMPTY_FORM: IssuerForm = { name: '', displayName: '', description: '', acmeServerUrl: '', eabKeyId: '', eabHmacKey: '' }

// The API answers validation and cluster errors with a short sentence; anything longer
// (an exception page in development) is not something to show an operator.
const apiError = (error: any, fallback: string): string => {
  const message = error?.response?.data ?? error?.data
  return typeof message === 'string' && message.trim() && message.length <= 400 ? message : fallback
}

// EF reads the UTC timestamps back without a zone suffix, and a bare ISO string
// parses as local time (same normalisation as ModulesOverview).
const formatDate = (iso?: string | null): string => {
  if (!iso) return ''
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso)
  return new Date(hasZone ? iso : `${iso}Z`).toLocaleDateString()
}

const isHttpsUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

// ACME ClusterIssuers the API creates in the cluster it runs on. A tenant hostname
// can be pointed at one of these on the tenant's Deploytime settings; hostnames that
// aren't keep the platform default issuer.
const CertificateIssuers: React.FC = () => {
  const { fetchApi } = useApi()
  const navigate = useNavigate()
  const roles = useContext(AuthRoleContext)
  const isAdmin = !!roles?.isAdmin

  const [issuers, setIssuers] = useState<CertificateIssuer[]>([])
  const [cluster, setCluster] = useState<CertificateIssuerClusterInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  // null = closed; editing is undefined while adding a new issuer.
  const [form, setForm] = useState<IssuerForm | null>(null)
  const [editing, setEditing] = useState<CertificateIssuer | undefined>(undefined)
  const [formError, setFormError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const [deleting, setDeleting] = useState<CertificateIssuer | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Revealed EAB HMAC keys by issuer id; only fetched when an admin asks for one.
  const [revealed, setRevealed] = useState<Record<string, string>>({})
  const [revealingId, setRevealingId] = useState<string | null>(null)
  const [isHelpOpen, setIsHelpOpen] = useState(false)

  const loadIssuers = useCallback(async () => {
    try {
      const [list, clusterInfo] = await Promise.all([
        fetchApi({ endpoint: '/api/certificate-issuers', method: 'GET' }),
        fetchApi({ endpoint: '/api/certificate-issuers/cluster', method: 'GET' })
      ])
      if (list === undefined) return // no token yet; the effect runs again once there is one
      setIssuers(Array.isArray(list) ? list : [])
      setCluster(clusterInfo ?? null)
      setLoadError(null)
    } catch (error: any) {
      setLoadError(apiError(error, 'The issuers could not be loaded.'))
    } finally {
      setIsLoading(false)
    }
  }, [fetchApi])

  useEffect(() => {
    loadIssuers()
  }, [loadIssuers])

  const visibleIssuers = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return issuers
    return issuers.filter((i) => [i.name, i.displayName, i.acmeServerUrl, i.eabKeyId ?? ''].some((v) => v.toLowerCase().includes(q)))
  }, [issuers, query])

  const openAdd = () => {
    setEditing(undefined)
    setForm(EMPTY_FORM)
    setFormError(null)
  }

  const openEdit = (issuer: CertificateIssuer) => {
    setEditing(issuer)
    setForm({
      name: issuer.name,
      displayName: issuer.displayName,
      description: issuer.description ?? '',
      acmeServerUrl: issuer.acmeServerUrl,
      eabKeyId: issuer.eabKeyId ?? '',
      eabHmacKey: ''
    })
    setFormError(null)
  }

  const closeForm = () => {
    if (isSaving) return
    setForm(null)
    setEditing(undefined)
  }

  const setField = (field: keyof IssuerForm, value: string) => setForm((prev) => (prev ? { ...prev, [field]: value } : prev))

  // Per-field problems, shown under the field once something has been typed.
  const errors = useMemo(() => {
    if (!form) return {} as Partial<Record<keyof IssuerForm, string>>
    const result: Partial<Record<keyof IssuerForm, string>> = {}
    const name = form.name.trim()
    if (!editing && name && (name.length > MAX_NAME_LENGTH || !ISSUER_NAME_PATTERN.test(name)))
      result.name = "Lowercase letters, digits, '-' and '.', starting and ending with a letter or digit."
    const server = form.acmeServerUrl.trim()
    if (server && !isHttpsUrl(server)) result.acmeServerUrl = 'Must be an https URL.'
    const keyId = form.eabKeyId.trim()
    const hmac = form.eabHmacKey.trim()
    if (hmac && !HMAC_PATTERN.test(hmac)) result.eabHmacKey = 'Use the base64url value the CA gave you.'
    else if (hmac && !keyId) result.eabKeyId = 'An HMAC key needs its key ID.'
    else if (keyId && !hmac && !editing?.hasEabHmacKey) result.eabHmacKey = 'A key ID needs its HMAC key.'
    return result
  }, [form, editing])

  const canSave =
    !!form &&
    !!form.name.trim() &&
    !!form.displayName.trim() &&
    !!form.acmeServerUrl.trim() &&
    Object.keys(errors).length === 0 &&
    !isSaving

  const save = async () => {
    if (!form || !canSave) return
    setIsSaving(true)
    setFormError(null)
    const body = {
      name: form.name.trim(),
      displayName: form.displayName.trim(),
      description: form.description.trim() || null,
      acmeServerUrl: form.acmeServerUrl.trim(),
      eabKeyId: form.eabKeyId.trim() || null,
      eabHmacKey: form.eabHmacKey.trim() || null
    }
    try {
      if (editing) {
        await fetchApi({ endpoint: `/api/certificate-issuers/${editing.id}`, method: 'PUT', body })
        customToast.success(`Updated ${body.displayName}`)
      } else {
        await fetchApi({ endpoint: '/api/certificate-issuers', method: 'POST', body })
        customToast.success(
          cluster?.enabled ? `Created ${body.name} in ${cluster.clusterName ?? 'the cluster'}` : `Added ${body.displayName}`
        )
      }
      setForm(null)
      setEditing(undefined)
      await loadIssuers()
    } catch (error: any) {
      setFormError(apiError(error, 'The issuer could not be saved.'))
    } finally {
      setIsSaving(false)
    }
  }

  const toggleHmac = async (issuer: CertificateIssuer) => {
    if (revealed[issuer.id] !== undefined) {
      setRevealed((prev) => {
        const next = { ...prev }
        delete next[issuer.id]
        return next
      })
      return
    }
    setRevealingId(issuer.id)
    try {
      const eab = await fetchApi({ endpoint: `/api/certificate-issuers/${issuer.id}/eab`, method: 'GET' })
      if (eab?.eabHmacKey) setRevealed((prev) => ({ ...prev, [issuer.id]: eab.eabHmacKey }))
    } catch (error: any) {
      customToast.error(apiError(error, `The HMAC key of ${issuer.name} could not be read.`))
    } finally {
      setRevealingId(null)
    }
  }

  const copy = async (value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value)
      customToast.success(`${what} copied`)
    } catch {
      customToast.error(`${what} could not be copied`)
    }
  }

  const iconButton = (iconName: string, label: string, onClick: () => void, isDisabled = false) => (
    <button
      type='button'
      aria-label={label}
      data-tooltip-id='global-tooltip'
      data-tooltip-content={label}
      disabled={isDisabled}
      onClick={onClick}
      style={{ background: 'none', border: 'none', padding: '2px', cursor: isDisabled ? 'default' : 'pointer', display: 'inline-flex' }}
    >
      <Icon iconName={iconName} size={14} color='var(--tma-text-muted)' />
    </button>
  )

  const reapply = async (issuer: CertificateIssuer) => {
    setBusyId(issuer.id)
    try {
      await fetchApi({ endpoint: `/api/certificate-issuers/${issuer.id}/apply`, method: 'POST' })
      customToast.success(`Re-applied ${issuer.name}`)
      await loadIssuers()
    } catch (error: any) {
      customToast.error(apiError(error, `${issuer.name} could not be re-applied.`))
    } finally {
      setBusyId(null)
    }
  }

  const confirmDelete = async () => {
    if (!deleting) return
    setIsSaving(true)
    setDeleteError(null)
    try {
      await fetchApi({ endpoint: `/api/certificate-issuers/${deleting.id}`, method: 'DELETE' })
      customToast.success(`Removed ${deleting.displayName}`)
      setDeleting(null)
      await loadIssuers()
    } catch (error: any) {
      setDeleteError(apiError(error, 'The issuer could not be removed.'))
    } finally {
      setIsSaving(false)
    }
  }

  const renderClusterChip = () => {
    if (!cluster) return null
    return cluster.enabled ? (
      <span style={pill('var(--tma-accent)')}>
        <Icon iconName='LegalCertificate' size={12} color='var(--tma-accent)' />
        {`Writes to ${cluster.clusterName || 'this cluster'}`}
      </span>
    ) : (
      <span
        style={pill('var(--tma-warning-text)')}
        data-tooltip-id='global-tooltip'
        data-tooltip-content='This environment has cluster sync switched off: issuers are only stored, not created in a cluster.'
      >
        Cluster sync off
      </span>
    )
  }

  const renderStatus = (issuer: CertificateIssuer) => {
    const state = CLUSTER_STATE[issuer.clusterState] ?? CLUSTER_STATE.Unknown
    return (
      <span style={pill(state.color)} data-tooltip-id='global-tooltip' data-tooltip-content={issuer.clusterMessage || state.hint}>
        {state.label}
      </span>
    )
  }

  const renderUsage = (issuer: CertificateIssuer) => (
    <tr>
      <td colSpan={6} style={{ ...tdStyle, background: 'var(--tma-page-bg)', paddingLeft: '28px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {issuer.usedBy.map((usage) => (
            <div key={usage.tenantId} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '10px' }}>
              <Link
                to={`/subscriptions/${usage.subscriptionId}/tenant/${usage.tenantId}`}
                style={{ color: 'var(--tma-accent)', fontWeight: 600, textDecoration: 'none' }}
              >
                {usage.tenantName}
              </Link>
              <span style={{ color: 'var(--tma-text-muted)', fontSize: '12px' }}>{usage.environment}</span>
              <span style={monoStyle}>{usage.hostnames.join(', ')}</span>
            </div>
          ))}
        </div>
      </td>
    </tr>
  )

  const renderRow = (issuer: CertificateIssuer) => {
    const inUse = issuer.usedBy.length > 0
    const isExpanded = expandedId === issuer.id
    const hostCount = issuer.usedBy.reduce((total, usage) => total + usage.hostnames.length, 0)
    const canReapply = isAdmin && !!cluster?.enabled && issuer.clusterState !== 'Ready' && issuer.clusterState !== 'NotManaged'
    return (
      <React.Fragment key={issuer.id}>
        <tr>
          <td style={tdStyle}>
            <Link
              to={`/certificates/requests?issuer=${encodeURIComponent(issuer.name)}`}
              style={{ color: 'var(--tma-accent)', fontWeight: 600, textDecoration: 'none' }}
              data-tooltip-id='global-tooltip'
              data-tooltip-content='Certificates and requests of this issuer'
            >
              {issuer.displayName}
            </Link>
            <div style={monoStyle}>{issuer.name}</div>
            {issuer.description && (
              <div style={{ color: 'var(--tma-text-muted)', fontSize: '12px', marginTop: '4px' }}>{issuer.description}</div>
            )}
          </td>
          <td style={tdStyle}>
            <div style={monoStyle}>{issuer.acmeServerUrl}</div>
          </td>
          <td style={tdStyle}>
            {issuer.eabKeyId ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={monoStyle}>{issuer.eabKeyId}</span>
                  {iconButton('CopyPaste', 'Copy the key ID', () => copy(issuer.eabKeyId ?? '', 'Key ID'))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--tma-text-muted)', fontSize: '12px' }}>
                  {revealed[issuer.id] !== undefined ? (
                    <span style={monoStyle}>{revealed[issuer.id]}</span>
                  ) : (
                    <span>{issuer.hasEabHmacKey ? 'HMAC key stored' : 'No HMAC key'}</span>
                  )}
                  {isAdmin &&
                    issuer.hasEabHmacKey &&
                    iconButton(
                      revealingId === issuer.id ? 'EmailActionSync1' : revealed[issuer.id] !== undefined ? 'ViewOff' : 'View1',
                      revealed[issuer.id] !== undefined ? 'Hide the HMAC key' : 'Show the HMAC key',
                      () => toggleHmac(issuer),
                      revealingId === issuer.id
                    )}
                  {revealed[issuer.id] !== undefined &&
                    iconButton('CopyPaste', 'Copy the HMAC key', () => copy(revealed[issuer.id], 'HMAC key'))}
                </div>
              </>
            ) : (
              <span style={{ color: 'var(--tma-text-muted)' }}>None</span>
            )}
          </td>
          <td style={tdStyle}>{renderStatus(issuer)}</td>
          <td style={tdStyle}>
            {inUse ? (
              <button
                type='button'
                onClick={() => setExpandedId(isExpanded ? null : issuer.id)}
                aria-expanded={isExpanded}
                style={{ ...pill('var(--tma-accent)'), cursor: 'pointer' }}
              >
                {`${hostCount} hostname${hostCount === 1 ? '' : 's'} · ${issuer.usedBy.length} tenant${issuer.usedBy.length === 1 ? '' : 's'}`}
                <Icon iconName={isExpanded ? 'ArrowUp1' : 'ArrowDown1'} size={10} color='var(--tma-accent)' />
              </button>
            ) : (
              <span style={{ color: 'var(--tma-text-muted)' }}>Not in use</span>
            )}
          </td>
          <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
            <div style={{ display: 'inline-flex', gap: '6px' }}>
              {isAdmin && (
                <Button2
                  text='Edit'
                  variant='subtle'
                  leftElement={<Icon iconName='Pencil1' size={14} />}
                  buttonSize='30px'
                  buttonRadius='8px'
                  type='button'
                  onClick={() => openEdit(issuer)}
                />
              )}
              {canReapply && (
                <Button2
                  text={busyId === issuer.id ? 'Applying…' : 'Re-apply'}
                  variant='subtle'
                  isDisabled={busyId === issuer.id}
                  leftElement={<Icon iconName='SynchronizeArrows1' size={14} />}
                  buttonSize='30px'
                  buttonRadius='8px'
                  type='button'
                  tooltip='Write this issuer to the cluster again'
                  onClick={() => reapply(issuer)}
                />
              )}
              {isAdmin && (
                <span
                  data-tooltip-id='global-tooltip'
                  data-tooltip-content={inUse ? 'Move its hostnames to another issuer before removing it.' : undefined}
                >
                  <Button2
                    text='Remove'
                    isDanger
                    variant='subtle'
                    isDisabled={inUse}
                    leftElement={<Icon iconName='Bin1' size={14} color='var(--tma-danger-text)' />}
                    buttonSize='30px'
                    buttonRadius='8px'
                    type='button'
                    onClick={() => {
                      setDeleteError(null)
                      setDeleting(issuer)
                    }}
                  />
                </span>
              )}
            </div>
          </td>
        </tr>
        {isExpanded && renderUsage(issuer)}
      </React.Fragment>
    )
  }

  const renderBody = () => {
    if (roles === null || isLoading) return <LoadingState text='Loading issuers…' />
    if (loadError)
      return (
        <div style={{ padding: '40px 16px', textAlign: 'center' }}>
          <Text textStyle='description' style={{ color: 'var(--tma-danger-text)' }} content={loadError} />
        </div>
      )
    if (issuers.length === 0)
      return (
        <div style={{ paddingTop: '60px', display: 'flex', justifyContent: 'center' }}>
          <EmptyState
            imageName='security'
            size='medium'
            title='No issuers yet'
            text={
              isAdmin
                ? 'Add an ACME issuer. TMA creates the ClusterIssuer, and tenants can then use it per hostname.'
                : 'An administrator can add the ACME issuers that tenants may use.'
            }
          />
        </div>
      )
    if (visibleIssuers.length === 0)
      return (
        <div style={{ padding: '40px 16px', textAlign: 'center' }}>
          <Text textStyle='description' textColor='grey' content={`No issuer matches "${query}".`} />
        </div>
      )
    return (
      <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, minWidth: '200px' }}>Issuer</th>
            <th style={{ ...thStyle, minWidth: '220px' }}>ACME server</th>
            <th style={{ ...thStyle, minWidth: '160px' }}>External account binding</th>
            <th style={thStyle}>Cluster</th>
            <th style={thStyle}>Used by</th>
            <th style={{ ...thStyle, textAlign: 'right' }} aria-label='Actions' />
          </tr>
        </thead>
        <tbody>{visibleIssuers.map(renderRow)}</tbody>
      </table>
    )
  }

  const fieldHint = (content: string) => (
    <Text textStyle='description' textColor='grey' content={content} style={{ display: 'block', marginTop: '4px', fontSize: '12px' }} />
  )

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'var(--app-height, 100vh)',
        overflow: 'hidden',
        background: 'var(--tma-panel-bg)',
        fontFamily: APP_FONT
      }}
    >
      <Toast />
      <div style={bandStyle}>
        <BandBackButton />
        <span style={{ color: 'var(--tma-band-text)', fontSize: '14px', fontWeight: 600, fontFamily: APP_FONT }}>Certificates</span>
        <span style={{ color: 'var(--tma-band-text-muted)', fontSize: '12px' }}>ACME issuers tenant hostnames can use</span>
      </div>

      <div style={toolbarStyle}>
        <div className='search-no-glow' style={{ width: '260px' }}>
          <TextField
            isSearchMode
            placeholder='Search issuers'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery('')}
            type='text'
            variant='default'
          />
        </div>
        {renderClusterChip()}
        <div style={{ flex: 1 }} />
        <Button2
          text='How it works'
          variant='subtle'
          leftElement={<Icon iconName='QuestionCircle' size={14} />}
          buttonSize='32px'
          buttonRadius='8px'
          type='button'
          onClick={() => setIsHelpOpen(true)}
        />
        {cluster?.enabled && (
          <Button2
            text='All certificates'
            variant='subtle'
            leftElement={<Icon iconName='LegalCertificate' size={14} />}
            buttonSize='32px'
            buttonRadius='8px'
            type='button'
            tooltip='Certificates and requests of every issuer, including the default'
            onClick={() => navigate('/certificates/requests')}
          />
        )}
        {isAdmin && (
          <Button2
            text='Add issuer'
            variant='contained'
            leftElement={<Icon iconName='Add' size={14} />}
            buttonSize='32px'
            buttonRadius='8px'
            type='button'
            onClick={openAdd}
          />
        )}
      </div>

      <div className='custom-scrollbar' style={{ flex: 1, overflow: 'auto' }}>
        {renderBody()}
      </div>

      <Modal
        isOpen={!!form}
        width='45%'
        height='auto'
        onClose={closeForm}
        onCancel={closeForm}
        hasHeaderBorder={true}
        headerTitle={editing ? `Edit ${editing.displayName}` : 'Add issuer'}
        onConfirm={save}
        isConfirmDisabled={!canSave}
        variant='prompt'
        labelConfirm={isSaving ? 'Saving…' : editing ? 'Save' : cluster?.enabled ? 'Create' : 'Add'}
        labelCancel='Cancel'
      >
        {form && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <TextFieldWrapper labelText='ClusterIssuer name' isRequired>
              <TextField
                value={form.name}
                placeholder='e.g. sectigo-ov'
                isDisabled={!!editing}
                errorMessage={errors.name}
                onChange={(e) => setField('name', e.target.value)}
                type='text'
                variant='default'
              />
              {fieldHint(
                editing
                  ? "The name is the issuer's identity in the cluster and can't be changed."
                  : 'The Kubernetes name. It cannot be changed later.'
              )}
            </TextFieldWrapper>
            <TextFieldWrapper labelText='Display name' isRequired>
              <TextField
                value={form.displayName}
                placeholder='e.g. Sectigo OV'
                onChange={(e) => setField('displayName', e.target.value)}
                type='text'
                variant='default'
              />
            </TextFieldWrapper>
            <TextFieldWrapper labelText='ACME server URL' isRequired>
              <TextField
                value={form.acmeServerUrl}
                placeholder='https://acme.sectigo.com/v2/OV'
                errorMessage={errors.acmeServerUrl}
                onChange={(e) => setField('acmeServerUrl', e.target.value)}
                type='text'
                variant='default'
              />
            </TextFieldWrapper>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <TextFieldWrapper labelText='EAB key ID' style={{ flex: 1, minWidth: '200px' }}>
                <TextField
                  value={form.eabKeyId}
                  placeholder='From the CA'
                  errorMessage={errors.eabKeyId}
                  onChange={(e) => setField('eabKeyId', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>
              <TextFieldWrapper labelText='EAB HMAC key' style={{ flex: 1, minWidth: '200px' }}>
                <TextField
                  value={form.eabHmacKey}
                  placeholder={editing?.hasEabHmacKey ? 'Stored. Leave empty to keep it' : 'From the CA'}
                  errorMessage={errors.eabHmacKey}
                  onChange={(e) => setField('eabHmacKey', e.target.value)}
                  type='password'
                  variant='default'
                />
              </TextFieldWrapper>
            </div>
            {fieldHint(
              'Leave both EAB fields empty for a CA without external account binding. The HMAC key is stored encrypted; admins can show it again from the list.'
            )}
            <TextFieldWrapper labelText='Description'>
              <TextField
                isTextArea
                value={form.description}
                placeholder='When to use it, who owns the CA account'
                onChange={(e) => setField('description', e.target.value)}
                type='text'
                variant='default'
              />
            </TextFieldWrapper>
            {formError && (
              <div
                style={{
                  marginTop: '8px',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid var(--tma-danger-border)',
                  background: 'var(--tma-danger-tint)'
                }}
              >
                <Text textStyle='description' style={{ color: 'var(--tma-danger-text)' }} content={formError} />
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isHelpOpen}
        width='50%'
        height='auto'
        onClose={() => setIsHelpOpen(false)}
        hasHeaderBorder={true}
        headerTitle='How certificate issuers work'
        variant='alert'
        onConfirm={() => setIsHelpOpen(false)}
        labelConfirm='Close'
        closeOnEsc
        closeOnOverlayClick
      >
        <CertificateIssuersHelp clusterName={cluster?.enabled ? cluster.clusterName || 'this cluster' : null} />
      </Modal>

      <Modal
        isOpen={!!deleting}
        width='45%'
        height='auto'
        onClose={() => !isSaving && setDeleting(null)}
        onCancel={() => !isSaving && setDeleting(null)}
        hasHeaderBorder={true}
        headerTitle={`Remove ${deleting?.displayName ?? ''}`}
        onConfirm={confirmDelete}
        isConfirmDisabled={isSaving}
        variant='confirm'
        labelConfirm={isSaving ? 'Removing…' : 'Remove'}
        labelCancel='Cancel'
      >
        <Text
          content={
            cluster?.enabled
              ? `This deletes the ClusterIssuer ${deleting?.name ?? ''} and its secrets from ${cluster.clusterName || 'the cluster'}, and removes it from the catalog.`
              : `${deleting?.name ?? ''} will be removed from the catalog.`
          }
        />
        {deleteError && (
          <div
            style={{
              marginTop: '12px',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid var(--tma-danger-border)',
              background: 'var(--tma-danger-tint)'
            }}
          >
            <Text textStyle='description' style={{ color: 'var(--tma-danger-text)' }} content={deleteError} />
          </div>
        )}
      </Modal>
    </div>
  )
}

export default CertificateIssuers
